/**
 * Pages are loaded on demand as separate chunks with hashed names. After a deploy, a tab still
 * running the previous build asks for chunk names that no longer exist (404), so the import
 * fails. The fix is to load the new build: reload the page once. A reload in the last
 * CHUNK_RELOAD_GUARD_MS means the chunk is missing even from a fresh build (or the network is
 * down), so the error is surfaced instead of reloading in a loop.
 */

const RELOAD_KEY = 'taim.chunkReloadAt';
export const CHUNK_RELOAD_GUARD_MS = 60_000;

/** Reloads unless one was done recently; false when it did not reload. */
function reloadOnce(reload: () => void, now: number): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
    if (now - last < CHUNK_RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // Without storage there is no way to tell a second failure from the first: do not risk a loop.
    return false;
  }
  reload();
  return true;
}

/** `load()`, or, when it fails, a single full page reload (the returned promise then never settles). */
export async function importOrReload<T>(
  load: () => Promise<T>,
  reload: () => void = () => window.location.reload(),
  now: number = Date.now(),
): Promise<T> {
  try {
    return await load();
  } catch (err) {
    if (!reloadOnce(reload, now)) throw err;
    // Keep the current screen (and the Suspense fallback) while the page reloads.
    return new Promise<T>(() => {});
  }
}
